import {DatePicker, Typography} from "antd"
import {BasePopup, KlassDropdown} from "components"
import {FormLabel} from "components/Form"
import {UNLIMITED_PAGE_SIZE} from "data/constants"
import {formatCurrency} from "helpers"
import {hasActiveRecurringPaymentForPlan} from "helpers/recurringPayments"
import {preventMinusCharacter} from "helpers/inputHelper"
import {usePaymentProviderConfigs} from "hooks/usePaymentProviderConfigs"
import {orderBy, sum} from "lodash"
import moment from "moment"
import StripeElementsProvider from "providers/StripeElementsProvider"
import {ReactNode, useEffect, useMemo, useState} from "react"
import {useTranslation} from "react-i18next"
import NumberFormat from "react-number-format"
import {studentAccountService} from "services"
import {
    PaymentPlan,
    PaymentPlanFrequency,
    PaymentPlanItem,
    PaymentPlanLinkedEntity,
    PaymentPlanStatus
} from "types/student-account/payment-plan"
import {
    CreateRecurringPaymentPayload,
    paymentCurrencyOptions,
    PaymentProvider,
    RecurringPaymentInterval,
    StudentPaymentSupportedCurrency,
    StudentRecurringPayment,
    StudentRecurringPaymentPlanItem
} from "types/student-account/student-payment"
import {AuthorizeNetRecurringSubscriptionForm} from "./AuthorizeNetRecurringSubscriptionForm"
import {DirectEmbedRecurringSubscriptionForm} from "./DirectEmbedRecurringSubscriptionForm"
import {PaypalRecurringSubscriptionForm} from "./PaypalRecurringSubscriptionForm"
import styles from "./RecurringPayments.module.css"
import paymentModalStyles from "sections/StudentAccount/Payment/PaymentModal/PaymentModal.module.css"
import StripeRecurringPaymentForm from "./StripeRecurringPaymentForm"

export type RecurringPaymentFromPaymentPlan = {
    remainingItems: PaymentPlanItem[]
    remainingPayments: number
    balance: number
    paymentPlanItems: CreateRecurringPaymentPayload["paymentPlanItems"]
    amount: number
    interval: RecurringPaymentInterval
    intervalCount: number
}

export const getRecurringPaymentFromPaymentPlan = (
    paymentPlan: Partial<PaymentPlan> | null | undefined
): RecurringPaymentFromPaymentPlan => {
    const remainingItems = orderBy(
        paymentPlan?.items?.filter((item) => item.paidAmount < item.amount) ?? [],
        ["dueDate"],
        ["asc"]
    )
    const accountId = paymentPlan?.accountId ?? null
    const paymentPlanItems = remainingItems.slice(0, 1).map((item) => ({
        paymentPlanId: item.paymentPlanId,
        paymentPlanItemId: item.paymentPlanItemId,
        amount: item.amount - item.paidAmount,
        accountId: item.paymentAccountId ?? accountId
    }))
    const amount = sum(paymentPlanItems.map((item) => item.amount))
    const frequency = paymentPlan?.frequency
    let interval = RecurringPaymentInterval.Month
    switch (frequency) {
        case PaymentPlanFrequency.Daily:
            interval = RecurringPaymentInterval.Day
            break
        case PaymentPlanFrequency.Weekly:
        case PaymentPlanFrequency.BiWeekly:
            interval = RecurringPaymentInterval.Week
            break
        case PaymentPlanFrequency.Monthly:
        default:
            interval = RecurringPaymentInterval.Month
    }
    const intervalCount = frequency === PaymentPlanFrequency.BiWeekly ? 2 : 1

    return {
        remainingItems,
        remainingPayments: remainingItems.length,
        balance: sum(remainingItems.map((item) => item.amount - item.paidAmount)),
        paymentPlanItems,
        amount,
        interval,
        intervalCount
    }
}

const DEFAULT_RECURRING_INTERVAL = RecurringPaymentInterval.Month
const DEFAULT_RECURRING_INTERVAL_COUNT = 1

type Props = {
    isShow: boolean
    onClose: () => void
    onSuccess: () => void
    profileId: number
    paymentPlan?: Partial<PaymentPlan> | null
    pendingRecurringPayment?: StudentRecurringPayment | null
    activeRecurringPayments?: StudentRecurringPayment[]
    title?: string
    description?: ReactNode
    initialAmount?: number
}

const RecurringPaymentPopup = ({
    isShow,
    onClose,
    onSuccess,
    profileId,
    paymentPlan = null,
    pendingRecurringPayment = null,
    activeRecurringPayments = [],
    title,
    description,
    initialAmount = 0
}: Props) => {
    const isPayPendingMode = !!pendingRecurringPayment
    const recurringFromPaymentPlan = useMemo(
        () => (paymentPlan?.items?.length ? getRecurringPaymentFromPaymentPlan(paymentPlan) : null),
        [paymentPlan]
    )
    const isBoundToPaymentPlan = !!recurringFromPaymentPlan && !isPayPendingMode
    const {t} = useTranslation(["payment", "common"])
    const [loadKey, setLoadKey] = useState(Math.random())
    const {isStripeEnabled, isPaypalEnabled, isAuthorizeNetEnabled, isDirectEmbedEnabled, paypalProviderConfig} =
        usePaymentProviderConfigs(profileId)
    const [amount, setAmount] = useState<number>(initialAmount)
    const [currency, setCurrency] = useState<StudentPaymentSupportedCurrency>("usd")
    const [interval, setInterval] = useState<RecurringPaymentInterval>(DEFAULT_RECURRING_INTERVAL)
    const [intervalCount, setIntervalCount] = useState<number>(DEFAULT_RECURRING_INTERVAL_COUNT)
    const [cancelAt, setCancelAt] = useState<moment.Moment | null>(null)

    const pendingPaymentPlanItems = useMemo((): CreateRecurringPaymentPayload["paymentPlanItems"] => {
        if (!pendingRecurringPayment?.paymentPlanItems?.length) return undefined
        return pendingRecurringPayment.paymentPlanItems.map((item: StudentRecurringPaymentPlanItem) => ({
            paymentPlanId: item.paymentPlanId,
            paymentPlanItemId: item.paymentPlanItemId,
            amount: item.amount,
            accountId: item.accountId ?? null
        }))
    }, [pendingRecurringPayment])

    // Declared early — referenced in useEffect and intervalOptions below. DirectEmbed self-manages
    // its own billing schedule (see recurring-payment-actions.ts) rather than a gateway "pending
    // until approved" state, so there's nothing to resume here in pay-pending mode — only offered
    // when creating a brand new subscription.
    const checkoutWithDirectEmbed =
        !isPayPendingMode && isDirectEmbedEnabled && !isStripeEnabled && !isPaypalEnabled && !isAuthorizeNetEnabled

    useEffect(() => {
        if (!isShow) return
        if (pendingRecurringPayment) {
            setAmount(pendingRecurringPayment.amountInCents / 100)
            setInterval(pendingRecurringPayment.interval)
            setIntervalCount(pendingRecurringPayment.intervalCount ?? 1)
            setCancelAt(pendingRecurringPayment.cancelAt ? moment(pendingRecurringPayment.cancelAt) : null)
            return
        }
        if (recurringFromPaymentPlan) {
            setAmount(recurringFromPaymentPlan.amount)
            setInterval(
                checkoutWithDirectEmbed && recurringFromPaymentPlan.interval === RecurringPaymentInterval.Day
                    ? RecurringPaymentInterval.Week
                    : recurringFromPaymentPlan.interval
            )
            setIntervalCount(recurringFromPaymentPlan.intervalCount)
            setCancelAt(null)
            return
        }
        setAmount(initialAmount)
        setInterval(DEFAULT_RECURRING_INTERVAL)
        setIntervalCount(DEFAULT_RECURRING_INTERVAL_COUNT)
        setCancelAt(null)
    }, [
        initialAmount,
        isShow,
        recurringFromPaymentPlan,
        checkoutWithDirectEmbed,
        pendingRecurringPayment?.recurringPaymentId,
        pendingRecurringPayment?.amountInCents,
        pendingRecurringPayment?.interval,
        pendingRecurringPayment?.intervalCount,
        pendingRecurringPayment?.cancelAt,
        pendingRecurringPayment
    ])

    useEffect(() => {
        if (!isShow) {
            setLoadKey(Math.random())
            setCancelAt(null)
        }
    }, [isShow])

    // DirectEmbed doesn't support daily billing (minimum interval is weekly).
    const intervalOptions = useMemo(
        () =>
            Object.values(RecurringPaymentInterval)
                .filter((value) => !(value === RecurringPaymentInterval.Day && checkoutWithDirectEmbed))
                .map((value) => ({
                    id: value,
                    name: t(`interval.${value}`)
                })),
        [t, checkoutWithDirectEmbed]
    )

    const [availablePlans, setAvailablePlans] = useState<PaymentPlan[]>([])
    const [selectedPlan, setSelectedPlan] = useState<PaymentPlan | null>(null)

    const isPaymentPlanSelected = isBoundToPaymentPlan || !!selectedPlan
    const showPaymentPlanSelector = !isPayPendingMode && !isBoundToPaymentPlan
    const amountEditable = !isPayPendingMode && !isPaymentPlanSelected
    const intervalEditable = !isPayPendingMode && !isPaymentPlanSelected

    useEffect(() => {
        if (!isShow || !showPaymentPlanSelector) return
        studentAccountService
            .getPaymentPlans({
                filter: {
                    profileIds: [profileId],
                    status: [PaymentPlanStatus.Active]
                },
                range: {pageSize: UNLIMITED_PAGE_SIZE, page: 1},
                linkedEntities: [PaymentPlanLinkedEntity.Items]
            })
            .then(({data}) => setAvailablePlans(data))
            .catch(() => setAvailablePlans([]))
    }, [isShow, profileId, showPaymentPlanSelector])

    useEffect(() => {
        if (!isShow) setSelectedPlan(null)
    }, [isShow])

    const selectedPlanRecurring = useMemo(
        () => (selectedPlan ? getRecurringPaymentFromPaymentPlan(selectedPlan) : null),
        [selectedPlan]
    )

    useEffect(() => {
        if (!showPaymentPlanSelector) return
        if (!selectedPlanRecurring) {
            setAmount(initialAmount)
            setInterval(DEFAULT_RECURRING_INTERVAL)
            setIntervalCount(DEFAULT_RECURRING_INTERVAL_COUNT)
            return
        }
        setAmount(selectedPlanRecurring.amount)
        setInterval(selectedPlanRecurring.interval)
        setIntervalCount(selectedPlanRecurring.intervalCount)
    }, [selectedPlanRecurring, showPaymentPlanSelector, initialAmount])

    const planOptions = useMemo(
        () =>
            availablePlans
                .filter((p) => !hasActiveRecurringPaymentForPlan(activeRecurringPayments, p.paymentPlanId))
                .map((p) => ({id: p.paymentPlanId, name: `${p.code} - ${p.title}`})),
        [availablePlans, activeRecurringPayments]
    )

    const effectivePaymentPlanItems = isPayPendingMode
        ? pendingPaymentPlanItems
        : isBoundToPaymentPlan
        ? recurringFromPaymentPlan?.paymentPlanItems
        : selectedPlanRecurring?.paymentPlanItems

    const effectivePaymentPlanId = isBoundToPaymentPlan
        ? paymentPlan?.paymentPlanId
        : selectedPlan?.paymentPlanId ?? undefined

    // Only meaningful when the recurring payment is derived from a payment plan's remaining unpaid
    // items (the balance/remainingPayments/amount breakdown below comes from that plan) — not shown
    // for a fully custom amount/interval with no plan behind it, or when paying an
    // already-prepared pending subscription.
    const effectiveRecurringSummary =
        !isPayPendingMode && isPaymentPlanSelected
            ? isBoundToPaymentPlan
                ? recurringFromPaymentPlan
                : selectedPlanRecurring
            : null

    const amountInCents = Math.round((amount || 0) * 100)
    const paypalExistingPrepare =
        isPayPendingMode && pendingRecurringPayment?.priceId
            ? {
                  recurringPaymentId: pendingRecurringPayment.recurringPaymentId,
                  paypalPlanId: pendingRecurringPayment.priceId
              }
            : undefined
    const checkoutWithStripe = isPayPendingMode
        ? isStripeEnabled && pendingRecurringPayment?.provider === PaymentProvider.Stripe
        : isStripeEnabled
    const checkoutWithPaypal = isPayPendingMode
        ? isPaypalEnabled && pendingRecurringPayment?.provider === PaymentProvider.Paypal
        : isPaypalEnabled && !isStripeEnabled
    const checkoutWithAuthorizeNet = isPayPendingMode
        ? isAuthorizeNetEnabled && pendingRecurringPayment?.provider === PaymentProvider.AuthorizeNet
        : isAuthorizeNetEnabled && !isStripeEnabled && !isPaypalEnabled
    return (
        <BasePopup isShow={isShow} onClose={onClose} leftIcon="CREDIT_CARD" width="60vw">
            <h1 className={styles.title}>
                {title || (isPayPendingMode ? t("title.payRecurringPayment") : t("title.addRecurringPayment"))}
            </h1>

            {description ? <div className="mb-24">{description}</div> : null}

            {showPaymentPlanSelector && (
                <div className="mb-24">
                    <FormLabel label={t("column.paymentPlan", "Payment Plan (optional)")} />
                    <KlassDropdown
                        isClearable
                        placeholder={t("column.paymentPlan", "Payment Plan (optional)")}
                        options={planOptions}
                        value={selectedPlan ? planOptions.find((o) => o.id === selectedPlan.paymentPlanId) : null}
                        onChange={(option) =>
                            setSelectedPlan(
                                option ? availablePlans.find((p) => p.paymentPlanId === option.id) ?? null : null
                            )
                        }
                    />
                </div>
            )}

            <div>
                <FormLabel label={t("label.amount")} />
                <NumberFormat
                    thousandSeparator
                    className={styles.input}
                    value={amount}
                    min={0}
                    decimalScale={2}
                    readOnly={isPayPendingMode || !amountEditable}
                    onValueChange={({floatValue}) => setAmount(floatValue ?? 0)}
                    onKeyDown={preventMinusCharacter}
                />
            </div>

            <div className="mt-24">
                <FormLabel label={t("label.currency", "Currency")} />
                <KlassDropdown
                    readOnly
                    isClearable={false}
                    options={paymentCurrencyOptions.filter((option) => option.id === "usd")}
                    value={paymentCurrencyOptions.find((option) => option.id === "usd")}
                />
            </div>

            <div className="mt-24">
                <FormLabel label={t("label.interval")} />
                <KlassDropdown
                    readOnly={isPayPendingMode || !intervalEditable}
                    isClearable={false}
                    options={intervalOptions}
                    value={intervalOptions.find((option) => option.id === interval)}
                    onChange={(option) => {
                        setInterval(option.id as RecurringPaymentInterval)
                        if (option.id !== RecurringPaymentInterval.Week) {
                            setIntervalCount(1)
                        }
                    }}
                />
            </div>

            <div className="mt-24">
                <FormLabel label={t("label.cancelAt", "Cancel At (optional)")} />
                <DatePicker
                    style={{width: "100%"}}
                    value={cancelAt}
                    onChange={setCancelAt}
                    disabledDate={(date) => date.isBefore(moment(), "day")}
                    allowClear
                />
            </div>

            {effectiveRecurringSummary && (
                <div className={paymentModalStyles.highlightBlock} style={{marginTop: 24}}>
                    {t("msg.recurringPayment.setupSummary", {
                        count: effectiveRecurringSummary.remainingPayments,
                        total: formatCurrency(effectiveRecurringSummary.balance),
                        amount: formatCurrency(effectiveRecurringSummary.amount)
                    })}
                </div>
            )}

            {checkoutWithStripe && (
                <div className="mt-24">
                    <StripeElementsProvider profileId={profileId} loadKey={loadKey}>
                        <StripeRecurringPaymentForm
                            profileId={profileId}
                            onSuccess={onSuccess}
                            amount={amount}
                            currency={currency}
                            interval={interval}
                            intervalCount={intervalCount}
                            cancelAt={cancelAt}
                            paymentPlanItems={effectivePaymentPlanItems}
                        />
                    </StripeElementsProvider>
                </div>
            )}

            {checkoutWithPaypal && (
                <div className="mt-24">
                    {paypalProviderConfig?.clientId ? (
                        <PaypalRecurringSubscriptionForm
                            key={loadKey}
                            profileId={profileId}
                            clientId={paypalProviderConfig.clientId}
                            amountInCents={amountInCents}
                            currency={currency}
                            interval={interval}
                            intervalCount={intervalCount}
                            cancelAt={cancelAt}
                            paymentPlanItems={effectivePaymentPlanItems}
                            paymentPlanId={effectivePaymentPlanId}
                            existingPrepare={paypalExistingPrepare}
                            onSuccess={onSuccess}
                        />
                    ) : (
                        <Typography.Paragraph type="danger">
                            {t("msg.recurringPayment.paypalMissingClientConfig")}
                        </Typography.Paragraph>
                    )}
                </div>
            )}

            {checkoutWithAuthorizeNet && (
                <div className="mt-24">
                    <AuthorizeNetRecurringSubscriptionForm
                        key={loadKey}
                        profileId={profileId}
                        amountInCents={amountInCents}
                        currency={currency}
                        interval={interval}
                        intervalCount={intervalCount}
                        cancelAt={cancelAt}
                        paymentPlanItems={effectivePaymentPlanItems}
                        paymentPlanId={effectivePaymentPlanId}
                        onSuccess={onSuccess}
                    />
                </div>
            )}

            {checkoutWithDirectEmbed && (
                <div className="mt-24">
                    <DirectEmbedRecurringSubscriptionForm
                        key={loadKey}
                        profileId={profileId}
                        amountInCents={amountInCents}
                        currency={currency}
                        interval={interval}
                        intervalCount={intervalCount}
                        cancelAt={cancelAt}
                        paymentPlanItems={effectivePaymentPlanItems}
                        paymentPlanId={effectivePaymentPlanId}
                        onSuccess={onSuccess}
                    />
                </div>
            )}
        </BasePopup>
    )
}

export default RecurringPaymentPopup
